import styled from '@xstyled/styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { select } from '~/lib/select';
import { useNextSequence } from '~/training/useNextSequence';
import { NextButtonEds, PlayButtonEds } from '~/ui/buttons';
import {
  useMediaPlayerStore,
  useUnlockAudio
} from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { SequenceStore, useSequenceStore } from './SequenceStore';
import { attentionPulse } from '~/ui/animation';
import { t } from 'ttag';
import { useGlobalMediaStore } from '~/ui/video-player/GlobalMediaProvider';
import { useQueryParams } from '~/lib/useQuery';
import { useLmsAdapter } from '~/lms';

interface Props {
  hideNext?: boolean;
}

const selector = (s: MediaPlayerStore) =>
  select(s, 'status', 'toggle', 'progress');

const sequenceSelector = (s: SequenceStore) =>
  select(s, 'blockers', 'autoContinue', 'needsScrollDown');

/** Bottom-center of the page, 8px from the viewport edge */
const CompleteStepsAnchor = styled.div`
  position: fixed;
  left: 50%;
  bottom: 8px;
  transform: translateX(-50%);
  z-index: 150;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CompleteStepsFrame = styled.div`
  position: relative;
  filter: drop-shadow(0px -2px 2px rgba(0, 0, 0, 0.05))
    drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.16));
  animation: ${attentionPulse} 1.4s ease-in-out 1;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

/** White border (light) / 50% white border (dark) + dark fill under the text */
const CompleteStepsPulse = styled.div`
  position: absolute;
  inset: 0;
  background: #464646;
  /* Figma stroke 5px: outer radius must be > border so the dark fill stays rounded
     (radius 4px + 5px border collapses the inner curve to a sharp corner). */
  border: 5px solid #ffffff;
  border-radius: 9px;
  box-sizing: border-box;
  pointer-events: none;
  z-index: 0;
`;

/**
 * Label over the chip. Figma text box is ~115×32 (2×16px lines):
 * "Complete the steps" / "to continue". max-width must fit the first line
 * (~130px at 12px) — 118px was too narrow and forced 3 lines.
 */
const CompleteStepsText = styled.p`
  position: relative;
  z-index: 1;
  /* Match Pulse border width so the label sits in the dark fill */
  margin: 5px;
  padding: 4px 12px;
  max-width: 130px;
  color: #fff;
  text-align: center;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  font-variant-numeric: lining-nums proportional-nums;
`;

const ButtonWrapper = styled.div`
  position: fixed;
  bottom: 8px;
  right: 8px;
  z-index: hud;

  @media (max-width: 580px) and (max-height: 300px) {
    bottom: 5px;
    right: 10px;
  }
`;

/** Visually hidden but kept in the a11y tree (global .sr-only uses
 *  visibility:hidden which VoiceOver skips). */
const LiveStatus = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/** Figma: appear after 3s pause; pulse; bottom-center */
const COMPLETE_STEPS_DELAY_MS = 3000;

const PleaseSelectOption = () => {
  const [playAnimation, setPlayAnimation] = useState(false);

  useEffect(() => {
    const timer = setTimeout(
      () => setPlayAnimation(true),
      COMPLETE_STEPS_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, []);

  if (!playAnimation) return null;

  return (
    <CompleteStepsAnchor role="status" aria-live="polite">
      <CompleteStepsFrame>
        <CompleteStepsPulse className="CompleteStepsPulse" aria-hidden="true" />
        <CompleteStepsText>{t`Complete the steps to continue`}</CompleteStepsText>
      </CompleteStepsFrame>
    </CompleteStepsAnchor>
  );
};

export const ComboButton = ({ hideNext }: Props) => {
  const next = useNextSequence();
  const params = useQueryParams();
  const { status, toggle, progress } = useMediaPlayerStore(selector, shallow);
  const unlockAudio = useUnlockAudio();
  const { blockers, autoContinue, needsScrollDown } =
    useSequenceStore(sequenceSelector);
  const lms = useLmsAdapter();
  const [disabled, setDisabled] = useState(false);
  const [nextAvailableMessage, setNextAvailableMessage] = useState('');
  const wasShowingNextRef = useRef(false);
  const isMountRef = useRef(true);

  const globalMedia = useGlobalMediaStore();
  const globalPlayer = globalMedia?.player;

  // CAT-1281: in LMS iframes scroll can be unreliable — never block Next on
  // overflow. Scroll hint still shows from Sequence; learner can still scroll.
  const hideNextForScroll = needsScrollDown && !lms.isAvailable;

  const isHidden = status === 'loading' || (autoContinue && status === 'done');
  const showNext =
    !isHidden &&
    (status === 'nomedia' || status === 'done') &&
    blockers.length === 0 &&
    !hideNext &&
    !hideNextForScroll &&
    params.get('removeAllButtons') !== 'true';

  // Hide while any media is actively playing — including nested players
  // (audio/video popups) whose captions portal over the sequence UI while the
  // outer MediaPlayerStore may already be `done` / `nomedia`.
  const showCompleteSteps =
    !isHidden &&
    (status === 'nomedia' || status === 'done') &&
    !(blockers.length === 0 && !hideNext) &&
    (hideNext || hideNext == undefined) &&
    !globalPlayer;

  // CAT-1036: announce when Next becomes available. Do not move focus —
  // the user may still be in a popup/dialog that just cleared the last blocker.
  // Do not reset the announce gate when Next is only hidden by temporary
  // blockers (Help/FAQ add `nextBtn`) — otherwise closing Help re-announces.
  useEffect(() => {
    if (status !== 'done' && status !== 'nomedia') {
      wasShowingNextRef.current = false;
      setNextAvailableMessage('');
      isMountRef.current = false;
      return;
    }

    if (!showNext) {
      setNextAvailableMessage('');
      isMountRef.current = false;
      return;
    }

    if (wasShowingNextRef.current) return;
    wasShowingNextRef.current = true;

    const isInitialNext = isMountRef.current;
    isMountRef.current = false;
    if (isInitialNext) return;

    // Clear first so SR detects a content change when we set the message.
    setNextAvailableMessage('');
    const timer = window.setTimeout(() => {
      setNextAvailableMessage(t`Slide complete. Next slide is available.`);
    }, 50);

    return () => window.clearTimeout(timer);
  }, [showNext, status]);

  const goNext = () => {
    // Must stay first and synchronous: the narration of the next sequence is
    // only played once it has loaded, by which time this gesture has expired.
    unlockAudio();

    if (!disabled) {
      setDisabled(true);
      if (globalPlayer) {
        try {
          globalPlayer.pause();
        } catch (error) {
          // Silently ignore player operation errors
        }
      }
      setTimeout(() => setDisabled(false), 1000);
      next();
    }
  };

  if (isHidden) {
    return null;
  }

  return (
    <>
      <ButtonWrapper>
        <LiveStatus role="status" aria-live="polite" aria-atomic="true">
          {nextAvailableMessage}
        </LiveStatus>
        {status === 'nomedia' || status === 'done' ? (
          blockers.length === 0 &&
          !hideNext &&
          !hideNextForScroll &&
          params.get('removeAllButtons') !== 'true' && (
            <NextButtonEds
              id="nextBtn"
              onClick={goNext}
              disabled={disabled}
              data-test-id="next-button"
            />
          )
        ) : (
          <PlayButtonEds state={status} onClick={toggle} progress={progress} />
        )}
      </ButtonWrapper>
      {showCompleteSteps && <PleaseSelectOption />}
    </>
  );
};
